import {uniqBy} from 'es-toolkit'

import {useAppDispatch, useAppSelector} from '../../hooks/redux'
import {closeModal} from '../../redux/features/modal'

import getCloudinaryUrl from '../../libs/cloudinary'

import {FinishGroupId, ModalMeta} from '../../types'

import ContentModal from '../../../../commons/src/components/content-modal/content-modal'
import Headline from '../../../../commons/src/components/headline'
import ImageContainer from '../../../../commons/src/components/image-container'
import MaterialImage from '../../../../commons/src/components/material-image'
import MaterialListEnumeration from '../../../../commons/src/components/material-list-enumeration/material-list-enumeration'
import Newlines from '../../../../commons/src/components/newlines'
import Paragraph from '../../../../commons/src/components/paragraph'
import Tag from '../../../../commons/src/components/tag'
import TruncatedListWithCount from '../../../../commons/src/components/truncated-list-with-count'

import DetailViewPartial from '../detail-view-partial'

type Props = {
  meta: ModalMeta
  finishGroupId: FinishGroupId
}

const FinishGroupModal = ({meta, finishGroupId}: Props) => {
  const dispatch = useAppDispatch()
  const finishGroup = useAppSelector(state => state.core.finishGroups[finishGroupId])
  const {materialId} = finishGroup

  const material = useAppSelector(state => state.core.materials[materialId])
  const materialConfigs = useAppSelector(state => state.core.materialConfigs)
  const colors = useAppSelector(state => state.core.colors)

  const colorsForFinishGroup = Object.values(materialConfigs)
    .filter(materialConfig => materialConfig.finishGroupId === finishGroupId)
    .map(materialConfig => colors[materialConfig.colorId])

  const tags = uniqBy([...finishGroup.tags, ...material.tags], tag => tag.id)

  return (
    <ContentModal
      fullscreenMobile={meta.fullscreenMobile}
      onClose={() => dispatch(closeModal())}
      headline={`Selected material: ${material.name}`}
    >
      <DetailViewPartial
        title={
          <>
            <TruncatedListWithCount space="l">
              {material.printingMethods.map(printingMethod => (
                <Tag key={printingMethod.id} size="l">
                  {printingMethod.name}
                </Tag>
              ))}
            </TruncatedListWithCount>
            <Headline label={finishGroup.name} size="xl" />
          </>
        }
        image={
          <ImageContainer
            source={getCloudinaryUrl(finishGroup.featuredImage, ['w_550'])}
            alt="Image of finish group"
          />
        }
        headerContent={null}
        content={
          <>
            <Headline label="Finish details" size="s" />
            <Paragraph>
              <Newlines text={finishGroup.description} />
            </Paragraph>
          </>
        }
        sidebar={
          <>
            <Headline label="Colors" size="s" />
            <TruncatedListWithCount maxLength={7} space="l">
              {colorsForFinishGroup.map(color => (
                <MaterialImage
                  key={color.id}
                  border
                  color={color.hexCode}
                  image={color.image && getCloudinaryUrl(color.image, ['w_10', 'h_10', 'c_fill'])}
                />
              ))}
            </TruncatedListWithCount>
            {tags.length > 0 && (
              <>
                <Headline label="Properties" size="s" className="u-margin-top-l" />
                <MaterialListEnumeration moreLabel="more">
                  {tags.map(tag => tag.name)}
                </MaterialListEnumeration>
              </>
            )}
          </>
        }
      />
    </ContentModal>
  )
}

export default FinishGroupModal
